/**
 * Relative-age formatting for the queue's "age" column (§18 /queue, §30 FIFO
 * tiebreak is by createdAtShopify, so age is operationally meaningful). Pure +
 * DOM-free for the node vitest project; `now` is injectable for deterministic
 * tests.
 */

/**
 * Format the age of an ISO timestamp as a compact relative string
 * ("just now", "5m", "3h", "2d", "5w"). Returns "—" for missing/invalid input.
 * Future timestamps clamp to "just now" (clock skew between Shopify and us).
 */
export function formatAge(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return "—";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "—";

  const deltaMs = now - then;
  if (deltaMs < 60_000) return "just now"; // also covers small negative skew

  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;

  const weeks = Math.floor(days / 7);
  return `${weeks}w`;
}

/** True once an order is older than `hours` — drives the "stale/at-risk" tint. */
export function isOlderThanHours(
  iso: string | null | undefined,
  hours: number,
  now: number = Date.now(),
): boolean {
  if (!iso) return false;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return false;
  return now - then > hours * 3_600_000;
}

/**
 * Age-based urgency for the queue's age column (Task 6). Returns a tone + label
 * only when an order has aged beyond a sensible threshold — so the cue means
 * something and isn't noise on fresh rows. Express/priority orders escalate at
 * tighter thresholds because they must ship faster. `null` = no flag.
 *
 * Thresholds (hours since the order date):
 *   standard          warn ≥ 24h, danger ≥ 48h
 *   express/priority  warn ≥ 6h,  danger ≥ 12h
 *
 * (`serviceClass` is taken loosely so this stays free of the enum import and
 * unit-testable in the node project.)
 */
export interface AgeUrgency {
  tone: "warn" | "danger";
  label: string;
}
export function ageUrgency(
  iso: string | null | undefined,
  serviceClass: string | null | undefined,
  now: number = Date.now(),
): AgeUrgency | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const hours = (now - then) / 3_600_000;
  const express = serviceClass === "EXPRESS" || serviceClass === "PRIORITY";
  const warnAt = express ? 6 : 24;
  const dangerAt = express ? 12 : 48;
  if (hours >= dangerAt) return { tone: "danger", label: "overdue" };
  if (hours >= warnAt) return { tone: "warn", label: "aging" };
  return null;
}
